<template>
  <div id="app">
    <AppPremiumLoader />
    <div class="main-wrapper">
      <router-view />
    </div>
    <SessionLockOverlay
      v-if="idleLockEnabled && sessionLock.isLocked && !isLockScreenRoute"
      mode="overlay"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/stores/authStore";
import { useSessionLockStore } from "@/stores/sessionLockStore";
import { useAppLoaderStore } from "@/stores/appLoaderStore";
import AppPremiumLoader from "@/components/ui/AppPremiumLoader.vue";
import SessionLockOverlay from "@/components/auth/SessionLockOverlay.vue";
import {
  isIdleLockEnabled,
  startIdleLock,
  stopIdleLock,
} from "@/services/idleLockService";

const auth = useAuthStore();
const sessionLock = useSessionLockStore();
const route = useRoute();

const isLockScreenRoute = computed(() => route.name === "lock-screen");
const idleLockEnabled = computed(() => isIdleLockEnabled());
const mobileAppQuery = window.matchMedia("(max-width: 991.98px)");

function syncMobileAppMode(event = mobileAppQuery) {
  document.body.classList.toggle("dq-mobile-app", event.matches);
  if (!event.matches) {
    document.body.classList.remove("slide-nav", "dq-mobile-drawer-open");
  }
}

function syncPlatformMode() {
  const isPlatform = (route.path || "").startsWith("/super-admin");
  document.body.classList.toggle("dq-platform", isPlatform);
}

onMounted(() => {
  syncMobileAppMode();
  syncPlatformMode();
  mobileAppQuery.addEventListener?.("change", syncMobileAppMode);

  if (!isIdleLockEnabled()) {
    stopIdleLock();
    sessionLock.reset();
  }

  if (auth.isAuthenticated) {
    auth.fetchCurrentUser?.();
    startIdleLock();
  }

  const path = route.path || "";
  if (
    route.meta?.guestOnly ||
    path.startsWith("/login") ||
    path.startsWith("/auth/verify-email") ||
    path.startsWith("/register") ||
    path.startsWith("/forgot-password") ||
    path.startsWith("/reset-password") ||
    path.startsWith("/set-password")
  ) {
    useAppLoaderStore().hide();
    window.dismissBootSplash?.();
    return;
  }

  window.setTimeout(() => {
    window.dismissBootSplash?.();
  }, 12000);
});

onBeforeUnmount(() => {
  mobileAppQuery.removeEventListener?.("change", syncMobileAppMode);
  document.body.classList.remove(
    "dq-mobile-app",
    "dq-mobile-drawer-open",
    "dq-platform",
    "slide-nav",
  );
});

watch(
  () => route.path,
  () => {
    syncPlatformMode();
  }
);

watch(
  () => auth.isAuthenticated,
  (isAuthenticated) => {
    if (!isIdleLockEnabled()) {
      stopIdleLock();
      sessionLock.reset();
      return;
    }

    if (isAuthenticated) {
      sessionLock.touchActivity();
      startIdleLock();
      return;
    }

    stopIdleLock();
    sessionLock.reset();
  },
  { immediate: true }
);
</script>
